import { makeModuleStateSelector, makeRedirectToSelector } from "@client/content/selectors";
import { changeOpacity } from "@client/lib/color";
import { getInherittedSettingValue, getMultiselectValue } from "@client/lib/settings";
import {
  type SettingsPropsParametricSelector,
  TargetSelector,
  combinedSettingsSelector,
  combinedStyleSelector,
  entitiesSelector,
  getColorSelector,
  getCompositeSettingSelector,
  getSettingValueSelector,
  makeEntityChildrenSelector,
  screenSelector,
  siteColorSelector
} from "@client/selectors";
import { type MainSettings, type OptionItem, ScreenType } from "@client/types";
import appInfo, { isStaffOrSuperUser } from "@common/appInfo";
import {
  type NetworkVolumeReportConfig,
  type NetworkVolumeReportStackBy
} from "@common/components/Report/Provider/Static/NetworkVolume";
import { type VideoBase } from "@common/controls/Video/component";
import { type Affiliate } from "@common/modules/affiliate/types";
import { type Data } from "@common/modules/data/types";
import { createSelector } from "reselect";
import { defaultState, previewState } from "./reducer";
import { type MemberAffiliateDashboard } from "./types";

export const localStateSelector = makeModuleStateSelector<MemberAffiliateDashboard.AppState>(
  defaultState,
  previewState
);

export const makeKpisSelector = () => {
  const childrenSelector = makeEntityChildrenSelector();

  return createSelector([entitiesSelector, childrenSelector, screenSelector], (entities, children, screen) => {
    return children
      .filter(
        (x) => x.data?.type === "kpi" && (x.data?.id !== "Shares" || appInfo.installedApps.includes("NetworkMarketing"))
      )
      .map((x): MemberAffiliateDashboard.View.KpiOption => {
        const name: Affiliate.KpiName = x.data!.id;
        const label = getInherittedSettingValue(entities[x.id].settings!, "", "name", screen);
        return {
          availability: getAvailability(entities[x.id].settings!, screen),
          name,
          label,
          value: x.id
        };
      });
  });
};

type SegmentSettings = {
  startLeg: number | undefined;
  endLeg: number | undefined;
  startLevel: number | undefined;
  endLevel: number | undefined;
  startBreakawayLevel: number | undefined;
  endBreakawayLevel: number | undefined;
  startDifferentialLevel: number | undefined;
  endDifferentialLevel: number | undefined;
  startRank: string | undefined;
  endRank: string | undefined;
};

const makeSegmentSettingsSelector = (prefix?: string) =>
  combinedSettingsSelector<SegmentSettings>(
    TargetSelector.Entity,
    [
      "startLeg",
      "endLeg",
      "startLevel",
      "endLevel",
      "startBreakawayLevel",
      "endBreakawayLevel",
      "startDifferentialLevel",
      "endDifferentialLevel",
      "startRank",
      "endRank"
    ],
    prefix
  );

type FilterSegment = "Rank" | FilterType;
type FilterType = "Rank" | "Level" | "Leg" | "BreakawayLevel" | "DifferentialLevel";

export const getStackByOptionItems = (groupType: FilterSegment, filterType: FilterType): OptionItem[] => {
  const items: OptionItem[] = [{ label: "Volume", value: "Volume" }];

  if (groupType === "Rank") {
    const segments = [
      { label: "Breakaway level", value: "BreakawayLevel" },
      { label: "Differential level", value: "DifferentialLevel" },
      { label: "Leg", value: "Leg" },
      { label: "Level", value: "Level" }
    ];

    if (!filterType) {
      items.push(...segments);
    } else {
      items.push(segments.find((x) => x.value === filterType)!);
    }
  } else {
    items.push({ label: "Rank", value: "Rank" });
  }

  return items;
};

const getVolumeFilter = (type: FilterType, settings: SegmentSettings): Data.NetworkVolumeFilter => {
  switch (type) {
    case "BreakawayLevel": {
      return {
        from: settings.startBreakawayLevel,
        to: settings.endBreakawayLevel,
        by: "breakawayLevel"
      };
    }
    case "DifferentialLevel": {
      return {
        from: settings.startDifferentialLevel,
        to: settings.endDifferentialLevel,
        by: "differentialLevel"
      };
    }
    case "Leg": {
      return {
        from: settings.startLeg,
        to: settings.endLeg,
        by: "leg"
      };
    }
    case "Level": {
      return {
        from: settings.startLevel,
        to: settings.endLevel,
        by: "level"
      };
    }
    case "Rank": {
      return {
        from: settings.startRank,
        to: settings.endRank,
        by: "rankName"
      };
    }
  }
};

const makeGroupSelector = (prefix?: string) => {
  const settingsSelector = makeSegmentSettingsSelector(prefix);
  const typeSelector = getSettingValueSelector<FilterType>(TargetSelector.Entity, "type", prefix);

  return createSelector([typeSelector, settingsSelector], getVolumeFilter);
};

export const makeNetworkVolumeConfigSelector = () => {
  const filterSettingsSelector = makeSegmentSettingsSelector("filter");
  const filterSegmentSelector = getSettingValueSelector<FilterSegment>(TargetSelector.Entity, "type", "filter");
  const groupSelector = makeGroupSelector("group");
  const settingsSelector = combinedSettingsSelector<{
    genealogyType: string | undefined;
    name: string;
    type: Data.NetworkVolumeAggregateBy;
    volume: Data.NetworkVolumeType[];
  }>(TargetSelector.Entity, [
    "genealogyType",
    "name",
    "type",
    { name: "volume", converter: getMultiselectValue, prop: "volume" }
  ]);

  return createSelector(
    [filterSettingsSelector, filterSegmentSelector, groupSelector, settingsSelector],
    (filterSettings, filterSegment, group, settings): Data.NetworkVolumeConfig => ({
      aggregateBy: settings.type,
      filter: getVolumeFilter(group.by === "rankName" ? filterSegment : "Rank", filterSettings),
      genealogyTypeId: settings.genealogyType,
      group,
      label: settings.name,
      volumeTypes: settings.volume
    })
  );
};

const reportColorsSelector = createSelector([siteColorSelector], (color) =>
  Array.from({ length: 8 }, (_, i) => changeOpacity(color, (1 / (i + 1)).toFixed(2)))
);

export const makeNetworkVolumeReportConfigSelector = () => {
  const networkVolumeConfigSelector = makeNetworkVolumeConfigSelector();
  const settingsSelector = combinedSettingsSelector<{
    chartType: Data.ReportType;
    showGrid: boolean;
    stackBy: NetworkVolumeReportStackBy | "";
    groupType: FilterSegment;
    filterType: FilterType;
  }>(TargetSelector.Entity, [
    //["chartType", normalizeDropdown],
    "stackBy",
    "showGrid",
    "groupType",
    "filterType"
  ]);

  return createSelector(
    [networkVolumeConfigSelector, settingsSelector, reportColorsSelector],
    (networkVolumeConfig, settings, colors): NetworkVolumeReportConfig => {
      const stackByOptions = getStackByOptionItems(settings.groupType, settings.filterType);

      return {
        ...networkVolumeConfig,
        colors,
        isNoGrid: !settings.showGrid,
        reportType: "Bar", // settings.chartType
        // stackBy may become invalid based on other setting values
        stackBy: stackByOptions.some((x) => x.value === settings.stackBy) ? settings.stackBy || null : null
      };
    }
  );
};

export const makeReportOptionsSelector = () => {
  const childrenSelector = makeEntityChildrenSelector();

  return createSelector(
    [entitiesSelector, childrenSelector, screenSelector],
    (entities, children, screen): MemberAffiliateDashboard.View.ReportOption[] => {
      return children
        .filter((x) => x.data?.type === "report")
        .map((x) => {
          const label = getInherittedSettingValue(entities[x.id].settings!, "", "name", screen);
          return {
            label,
            value: x.id,
            reportId: x.data!.id,
            availability: getAvailability(entities[x.id].settings!, screen)
          };
        });
    }
  );
};

export const makeWidgetStyleSelector = (): SettingsPropsParametricSelector => {
  const backgroundColorSelector = getColorSelector(TargetSelector.Entity, "backgroundColor", "widget");

  const styleSelector = combinedStyleSelector(TargetSelector.Entity, ["border"], "widget");

  return createSelector(
    [backgroundColorSelector, styleSelector],
    (backgroundColor, style): React.CSSProperties => ({
      ...style,
      backgroundColor
    })
  );
};

const makeModuleVideoSelector = (prefix?: string) => {
  const videoSettingsSelector = combinedSettingsSelector(
    TargetSelector.Entity,
    [
      { name: "controls", prop: "showControls" },
      "loop",
      "mute",
      { name: "autoplay", prop: "autoPlay" },
      { name: "type", prop: "type" },
      { name: "vimeoID", prop: "vimeoId" },
      { name: "youTubeID", prop: "youTubeId" },
      { name: "wistiaID", prop: "wistiaId" }
    ],
    prefix
  );
  return createSelector([videoSettingsSelector], (videoSettings): VideoBase & { source?: string } => ({
    ...videoSettings,
    source:
      videoSettings.type === "youtube"
        ? videoSettings.youTubeId
          ? "https://www.youtube.com/watch?v=" + videoSettings.youTubeId
          : undefined
        : videoSettings.type === "vimeo"
        ? videoSettings.vimeoId
          ? "https://vimeo.com/" + videoSettings.vimeoId
          : undefined
        : videoSettings.wistiaId
        ? "https://home.wistia.com/medias/" + videoSettings.wistiaId
        : undefined
  }));
};

export const makeWelcomeSelector = () => {
  const welcomeSettingSelector = getCompositeSettingSelector<MemberAffiliateDashboard.View.WelcomeSettings>(
    TargetSelector.Entity,
    "welcome"
  );
  const videoSelector = makeModuleVideoSelector("welcome.video");
  const redirectSelector = makeRedirectToSelector(TargetSelector.Entity, "welcome");

  return createSelector(
    [welcomeSettingSelector, videoSelector, redirectSelector],
    (welcomeSetting, video, redirectToTerms): MemberAffiliateDashboard.View.WelcomeSettings | null => {
      if (!welcomeSetting) return null;

      return {
        ...welcomeSetting,
        ...video,
        hasTerms: !!redirectToTerms
      };
    }
  );
};
const getAvailability = (settings: MainSettings, screen: ScreenType): Affiliate.Availability | undefined => {
  if (isStaffOrSuperUser()) return;

  const rankIds = getMultiselectValue(getInherittedSettingValue(settings, "", "availability", screen));
  return {
    rankIds: rankIds && rankIds.length ? rankIds : null
  };
};
